// Full-screen overlay while POST /v1/hotels/search/ is in flight.
//
// Same .loader-overlay / .loader-card shell as the flight SearchingLoader, with
// the route-and-plane animation swapped for a city skyline: hotel search is a
// place, not a journey, and a plane arcing between two airports would be lying
// about what's happening.
//
// The search genuinely takes ~5s (the supplier fans out to its own suppliers),
// which is why this exists at all rather than a spinner.

import { fmtDateLong, nightsLabel } from "../lib/hotelFormat";
import { KairaMark } from "./KairaMark";

interface Props {
  city: string;
  country?: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  roomsLabel: string;
}

export function HotelSearchingLoader(props: Props) {
  return (
    <div className="loader-overlay">
      <div className="loader-card hotel-loader-card">
        <div className="hotel-loader-stage">
          <svg className="hotel-loader-svg" viewBox="0 0 400 150" preserveAspectRatio="xMidYMid meet">
            {/* Skyline. Each tower animates its windows on its own delay, so the
                city reads as "waking up" rather than blinking in unison. */}
            <g className="skyline">
              <Tower x={54} width={56} height={70} delay={0} />
              <Tower x={122} width={44} height={104} delay={0.35} />
              <Tower x={178} width={64} height={86} delay={0.7} />
              <Tower x={254} width={48} height={116} delay={0.2} />
              <Tower x={314} width={40} height={64} delay={0.55} />
            </g>
            <line className="hotel-loader-ground" x1="30" y1="130" x2="370" y2="130" />
            <g className="hotel-loader-scan">
              <rect x="-60" y="10" width="60" height="120" />
            </g>
          </svg>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div className="loader-kaira" style={{ background: "transparent", boxShadow: "none", border: "none" }}>
            <KairaMark size={36} />
          </div>
        </div>

        <div className="status-stage">
          <span className="status-text">Checking <span className="serif">availability</span> in {props.city}...</span>
          <span className="status-text">Pricing every room the property will sell</span>
          <span className="status-text">Reading <span className="serif">cancellation</span> terms...</span>
          <span className="status-text">Almost there...</span>
        </div>

        <div className="stage-dots">
          <span className="sd" /><span className="sd" /><span className="sd" /><span className="sd" />
        </div>

        {/* Chips, not one dot-separated run. A hotel search carries far more
            context than a flight one ("Dubai, United Arab Emirates" alone is as
            long as a whole flight context line), so the row has to wrap — and a
            wrapping run of text separated by "·" leaves an orphaned dot at the
            start of a line. Each fact gets its own chip and wrapping reads as
            deliberate at any width. */}
        <div className="search-context hotel-context">
          <span className="row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <span>{props.city}{props.country ? `, ${props.country}` : ""}</span>
          </span>
          <span className="row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <span>
              {fmtDateLong(props.checkIn)} <span className="ctx-arrow">→</span>{" "}
              {fmtDateLong(props.checkOut)} · {nightsLabel(props.nights)}
            </span>
          </span>
          <span className="row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>{props.roomsLabel}</span>
          </span>
        </div>

        <p style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 12 }}>
          This usually takes 3–8 seconds.
        </p>
      </div>
    </div>
  );
}

function Tower({ x, width, height, delay }: {
  x: number; width: number; height: number; delay: number;
}) {
  const top = 130 - height;
  // Windows on a 14px grid, inset 8px, so a narrow tower gets one column and a
  // wide one gets three without any per-tower tuning.
  const cols = Math.max(Math.floor((width - 16) / 14), 1);
  const rows = Math.max(Math.floor((height - 16) / 16), 1);
  const windows = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      windows.push(
        <rect
          key={`${r}-${c}`}
          className="hotel-window"
          x={x + 8 + c * 14}
          y={top + 10 + r * 16}
          width={7}
          height={8}
          style={{ animationDelay: `${delay + (r * cols + c) * 0.12}s` }}
        />,
      );
    }
  }
  return (
    <g>
      <rect className="hotel-tower" x={x} y={top} width={width} height={height} rx={3} />
      {windows}
    </g>
  );
}
